import styles from './image-field.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image, Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { Button, IconButton } from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal/modal.component';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';
import { FileUploadV2 } from 'src/components/core/fileupload/FileUploadV2.component';
import { colorPalette } from 'shared/constants';
import { SUPPORTED_IMG_FORMATS } from 'shared/constants/assets.constants';
import { getImageDimensions } from 'shared/utils';
import { useDispatch } from 'react-redux';
import { uploadDocument } from 'shared/store/actions/knowledge.items.actions';
import classNames from 'classnames';

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.name
 * @param {string} props.value
 * @param {{ width: number, height: number }} [props.dimensions]
 * @param {(value: string) => Promise<void>} props.applyChanges
 * @param {object} props.lang
 * @param {{ width?: string, height?: string }} [props.previewSize]
 */
export function ImageField({ label, name, value, dimensions, applyChanges, lang, previewSize }) {
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(undefined);
  const previewUrlRef = useRef('');
  const triggerRef = useRef(null);
  const dispatch = useDispatch();

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
  }, []);

  useEffect(() => revokePreview, [revokePreview]);

  const validateDimensions = async url => {
    if (!url || !dimensions) return undefined;
    try {
      const { width, height } = await getImageDimensions(url);
      if (width !== dimensions.width || height !== dimensions.height) {
        return lang.IMAGE_DIMENSION_ERROR.replace('{WIDTH}', dimensions.width)
          .replace('{HEIGHT}', dimensions.height)
          .replace('{CURRENT_WIDTH}', width)
          .replace('{CURRENT_HEIGHT}', height);
      }
    } catch {
      return lang.IMAGE_DIMENSION_ERROR_GENERIC.replace('{WIDTH}', dimensions.width).replace(
        '{HEIGHT}',
        dimensions.height,
      );
    }
    return undefined;
  };

  const resetModal = () => {
    revokePreview();
    setSelectedFile(null);
    setPreviewUrl('');
    setError(undefined);
    setUploading(false);
  };

  const handleCloseModal = () => {
    if (uploading) return;
    setModalOpen(false);
    resetModal();
  };

  const handleFileSelected = async ({ file }) => {
    revokePreview();
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setSelectedFile(file);
    setPreviewUrl(url);
    const validationError = await validateDimensions(url);
    setError(validationError);
  };

  const handleTryAnother = () => {
    revokePreview();
    setSelectedFile(null);
    setPreviewUrl('');
    setError(undefined);
  };

  const handleConfirm = async () => {
    setUploading(true);
    setError(undefined);
    try {
      const res = await dispatch(uploadDocument(selectedFile));
      if (!res?.url) {
        setError(lang.UPLOAD_FAILED);
        setUploading(false);
        return;
      }

      setModalOpen(false);
      resetModal();
      setLoading(true);
      await applyChanges(res.url);
      setLoading(false);
      triggerRef.current?.focus();
    } catch {
      setError(lang.UPLOAD_FAILED);
      setUploading(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    await applyChanges('');
    setLoading(false);
  };

  const popconfirmProps = useAccessiblePopconfirm({
    onConfirm: handleClear,
  });

  return (
    <div className={styles.root}>
      <div className={styles.previewHeader}>
        <label className={styles.label}>{label}</label>
        <IconButton
          label={lang.EDIT}
          disabled={loading}
          onClick={() => {
            triggerRef.current = document.activeElement;
            setModalOpen(true);
          }}>
          <FontAwesomeIcon icon={faPencil} color={colorPalette.primary} />
        </IconButton>
        {value && (
          <Popconfirm
            icon={null}
            title={lang.DELETE}
            description={lang.DELETE_IMAGE_CONFIRMATION}
            cancelText={lang.CANCEL}
            okText={lang.DELETE}
            okType="danger"
            okButtonProps={{ type: 'primary' }}
            cancelButtonProps={{ type: 'default' }}
            {...popconfirmProps}>
            <IconButton label={lang.DELETE} disabled={loading}>
              <FontAwesomeIcon icon={faTrashCan} color={colorPalette.primary} />
            </IconButton>
          </Popconfirm>
        )}
      </div>
      {value ? (
        <Image
          className={styles.previewImage}
          width={previewSize?.width ?? 'auto'}
          height={previewSize?.height ?? '3.75rem'}
          src={value}
          alt={label}
          title={label}
          preview={{
            cover: <FontAwesomeIcon color={colorPalette.neutral1} icon={faEye} size="lg" />,
          }}
        />
      ) : (
        <p className={classNames(styles.previewText, { [styles.previewTextDisabled]: loading })}>
          {lang.UNASSIGNED}
        </p>
      )}
      <Modal
        title={label}
        open={modalOpen}
        onCancel={handleCloseModal}
        onSubmit={handleConfirm}
        cancelText={lang.CANCEL}
        submitText={lang.CONFIRM ?? 'Confirm'}
        loading={uploading}
        submitDisabled={!selectedFile || !!error}>
        {!selectedFile ? (
          <FileUploadV2
            accept={Object.keys(SUPPORTED_IMG_FORMATS).join(',')}
            customRequest={handleFileSelected}
            showUploadList={false}
            hint={dimensions ? `${dimensions.width}x${dimensions.height}px` : undefined}
          />
        ) : (
          <div className={styles.modalPreview}>
            <img
              src={previewUrl}
              alt={`${label} preview: ${selectedFile.name}`}
              className={styles.modalPreviewImage}
            />
            <Button size="small" type="link" onClick={handleTryAnother} disabled={uploading}>
              {lang.TRY_ANOTHER ?? 'Try Another'}
            </Button>
            {error && (
              <span className={styles.error} role="alert">
                {error}
              </span>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
